(ns graph-ui.web.island-view
  "The embedded live preview widget — PLAN-graph-ui-v0-006-01 §Phase 5b/5b.2,
   ADR-ikc-v3-050-01. Renders ANOTHER island read-only inside the current one:
   the Studio design canvas (NetBeans-Matisse north star).

   It reuses the SHARED pure render pipeline (graph-ui.web.bind/resolve-bindings
   → graph-ui.web.interpret/render-tree → the render fn), but drives it from two
   sources deliberately kept apart:

     STRUCTURE comes from the editor DRAFT (graph-ui.web.draft) — the description
     :text-area's live buffer — so the canvas repaints AS YOU TYPE, falling back
     to the attached island's saved tree when the draft is empty or unparseable.

     DATA comes from a REAL embedded SESSION of its own: the widget attaches to
     the previewed island under its own preview session-id (:mode :canvas — a
     reader, no compose lock), so the island's session-scoped panes resolve
     against ITS session, never Studio's (the v1 bug George caught). It repaints
     on that island's :ui/widget-tree-changed, and the header carries Reconnect
     (re-attach) and New session (drop + fresh session-id + attach) — the canvas
     chrome. A preview is a reader and takes no lock, so it can never steal the
     one the composer holds (ADR-graph-ui-v0-005-01 §7).

   Self-embed guard: any :island-view INSIDE the previewed tree becomes a
   placeholder, so previewing Studio renders Studio's whole layout with only the
   ONE nested canvas stubbed — never a recursion.

   The render fn is PASSED IN (from render.cljs) rather than required, so this ns
   does not require render.cljs, which requires this ns — no CLJS cycle. The
   widget uses the REAL bus for attach/detach/subscribe AND for rendering the
   previewed tree — the preview is an interactive live mirror of its own
   session, not a dead picture. A self-embed guard (neutralize-nested-previews)
   stubs a nested :island-view, and a second guard filters the PARENT Studio
   session out of the session selector so the preview can never mirror/join the
   parent's session (the two-surfaces-one-session conflict)."
  (:require [reagent.core :as r]
            [clojure.walk :as walk]
            [graph-ui.web.bind :as bind]
            [graph-ui.web.interpret :as interpret]
            [graph-ui.web.draft :as draft]
            [graph-ui.web.locate :as locate]
            [graph-ui.web.activity :as activity]))

;; @feature F-graph-ui-v0-001-01-ui-quality-system
;; @adr ADR-ikc-v3-050-01-studio-self-hosting

(declare refresh-sessions! join-session!)

(defn- neutralize-nested-previews
  "Replace every :island-view node inside a previewed tree with a muted
   placeholder — the self-embed guard. Studio-previewing-Studio thus renders its
   whole layout with only THIS one pane stubbed, never a recursion."
  [tree]
  (walk/postwalk
    (fn [x]
      (if (and (map? x) (= :island-view (:widget x)))
        {:widget :text :value "▣ live-preview pane (not nested here)" :tone :muted}
        x))
    tree))

(def ^:private reading-commands
  "The commands a PREVIEWED island may still send. Reads, and the subscribe
   that keeps a bound value fresh."
  #{:ui/read :ui/read-many :ui/subscribe :ui/unsubscribe :ui/materialise})

(defn reading-bus
  "The bus a PREVIEWED island is rendered with: it answers reads and refuses
   every other command.

   A PREVIEW IS A READER, AND IT WAS NOT ONE. Measured 2026-09-21 with
   dev/studio_phase9_probe.mjs, on a probe bus: ONE click inside a previewed
   reader-v3 dispatched :ui/new-note, and a :page frame titled \"Untitled\"
   was created. The previewed tree was handed the REAL bus, so every gesture
   drawn inside it was a real gesture on a real island.

   IT IS THE BUS AND NOT THE WIDGETS, because a widget cannot know it is being
   previewed. Forty-odd widget kinds each dispatch their own :on-gesture, and a
   guard in each of them is forty places to forget. One seam refuses once.

   READS STILL GO, because a preview draws DATA and a read is not a gesture.
   Everything else is dropped and RECORDED, so a person watching the activity
   log can tell a refused gesture from a dead button (PRINCIPLE-ikc-v3-010-01).

   THE CANVAS CHROME IS NOT AFFECTED. Reconnect, the session selector and the
   zoom control are the PREVIEW's own controls and keep the real bus — they are
   supposed to reach it."
  [real-bus]
  (when real-bus
    #js {"dispatch"
         (fn [command cb]
           (if (contains? reading-commands (:type command))
             (js-invoke real-bus "dispatch" command cb)
             (do (activity/record!
                   {:kind :ui :label "preview refused a gesture"
                    :detail (str (:type command))})
                 (when cb (cb {:error :preview-is-read-only
                               :command (:type command)}))
                 nil)))
         "subscribe"
         (fn [pattern callback] (js-invoke real-bus "subscribe" pattern callback))
         ;; A PREVIEW EMITS NOTHING. An event from a previewed island would
         ;; reach every other surface as though the island itself had spoken.
         "emit" (fn [_event] nil)}))

(defn pairs-to-reread
  "PURE: the (source, args) pairs of the preview that one :ui/source-changed
   announcement makes stale. `subscriptions` is the :subscriptions vector of the
   preview's attach reply, `preview-sid` the session the preview shows.

   THE RENDER VIEW FOLLOWS ITS SESSION (PLAN-ikc-v3-M4d-001-01 phase 8, the
   brief of 2026-09-27). The preview read its sources once, in the attach
   reply, and again only when the island's TREE changed. So Studio's render
   view of gi showed a session as it was at the join, while the gi island on
   the same session went on changing. Measured in a browser on a probe bus.

   A pair qualifies when the announcement names its source, and when neither
   the announcement nor the pair names a session other than the preview's. That
   is the rule the main client applies in core.cljs on-source-changed-one: an
   announcement for another session is not this view's business."
  [subscriptions preview-sid event]
  (let [named   (if-let [many (seq (:sources event))]
                  many
                  (when (:source event) [{:source (:source event) :args (:args event)}]))
        mine?   (fn [sid] (or (nil? sid)
                              (= (bind/canon-uuid sid) (bind/canon-uuid preview-sid))))]
    (vec (distinct
          (for [{:keys [source args]} named
                :when (mine? (or (:session-id args) (:session-id event)))
                pair  subscriptions
                :when (and (= source (:source pair))
                           (mine? (get-in pair [:args :session-id])))]
            (select-keys pair [:source :args]))))))

(defn- reread!
  "Read `pairs` again with ONE :ui/read-many and put each answer into the
   preview's own :sources, under the key the render pipeline reads. An answer
   that is an {:error ...} is dropped, so the preview keeps the value it had."
  [state real-bus pairs]
  (when (and real-bus (seq pairs))
    (js-invoke real-bus "dispatch"
      {:type :ui/read-many :reads pairs}
      (fn [reply]
        (let [values (if (map? reply) (:values reply) reply)]
          (when (sequential? values)
            (swap! state update :sources
                   (fn [m]
                     (reduce (fn [acc [{:keys [source args]} v]]
                               (if (and (map? v) (:error v))
                                 acc
                                 (assoc acc (bind/snapshot-key source args) v)))
                             (or m {})
                             (map vector pairs values))))))))))

(defn- read-file-tree!
  "Ask the server for the open subject's tree, EXPANDED FROM ITS FILES
   (:ui-preview-tree, PLAN-graph-ui-v0-016-02 phase 9).

   WHY IT IS NOT THE ATTACHED TREE. :ui/attach answers the tree the GRAPH
   holds, which is the state the island was last SEEDED at. An edit to a page
   file or a pattern file does not appear there until somebody re-seeds — and
   the Studio island is create-only, so that is a deliberate act. A person
   editing a declaration was shown the declaration as it was BEFORE the edit.

   IT IS THE SEEDER'S OWN EXPANSION, ui.blocks/expand over ui.patterns, so the
   preview and the seed cannot disagree about what a file means.

   KEYED BY THE STUDIO SESSION, not the preview session: the source answers for
   whichever subject STUDIO has open.

   nil IS A LEGAL ANSWER — a token owns no page, and an island seeded before
   the :composition slot existed names no composition. The preview then draws
   the attached tree, exactly as it did before this existed."
  [state real-bus studio-sid]
  (when real-bus
    (js-invoke real-bus "dispatch"
      {:type :ui/read-many
       :reads [{:source :ui-preview-tree :args {:session-id studio-sid}}]}
      (fn [reply]
        ;; :ui/read-many ANSWERS A PLAIN VECTOR, in the order asked for. A
        ;; source whose read threw answers with its own {:error …} in that
        ;; slot, and an error is not a tree — so it is dropped and the preview
        ;; keeps the attached one.
        (let [v (first (if (map? reply) (:values reply) reply))]
          (swap! state assoc :file-tree (when (and (map? v) (:widget v)) v)))))))

(defn- detach!
  "Release the preview's current session, if attached — so re-attaching or
   dropping a session never leaks a presentation frame on the previewed island."
  [state real-bus]
  (let [{:keys [island-id preview-sid presentation-id status]} @state]
    (when (and real-bus (= :attached status) island-id preview-sid)
      ;; BY :presentation-id, so the preview closes ITS view. The preview can
      ;; join the session of another surface, and a detach by :session-id
      ;; alone closes the first presentation of that session, which can be
      ;; the other surface's (PLAN-ikc-v3-M4d-001-01 phase 8).
      (js-invoke real-bus "dispatch"
        (cond-> {:type :ui/detach :island-id island-id :session-id preview-sid}
          presentation-id (assoc :presentation-id presentation-id))
        (fn [_] nil)))
    (swap! state assoc :status :idle)))

(defn- attach!
  "(Re)establish the preview session: detach the current one, resolve WHICH island
   the Studio session has selected (:ui/preview-context), then attach to it under
   the preview session-id (:mode :canvas — a reader) and store its live
   description/sources/rules. `new-session?` mints a fresh preview session-id
   first (the 'New session' button); otherwise it re-attaches the same one
   ('Reconnect', a selection change, or a data change on the previewed island)."
  [state real-bus studio-sid new-session?]
  (when real-bus
    (detach! state real-bus)
    (when new-session? (swap! state assoc :preview-sid (random-uuid)))
    (swap! state assoc :status :attaching)
    ;; CHEAP identity read: which island is selected? — NOT :ui/preview-context,
    ;; which materialises the tree + snapshots every bound source (5-10s) and was
    ;; being fired 4x per select just for this id (postmortem via :activity-log,
    ;; 2026-07-18). The render data comes from :ui/attach's own reply below.
    (js-invoke real-bus "dispatch"
      {:type :ui/selected-island :session-id studio-sid}
      (fn [sel]
        (if-let [iid (:island-id sel)]
          (let [sid (:preview-sid @state)]
            ;; Set :island-id optimistically so a source-changed that fires while
            ;; this attach is still in flight sees the new id and does NOT re-attach.
            (swap! state assoc :island-id iid)
            (js-invoke real-bus "dispatch"
              {:type :ui/attach :island-id iid :mode :canvas :session-id sid}
              (fn [reply]
                (if (:error reply)
                  (do (js/console.warn "[ikc3] island-view: canvas attach failed"
                                       (pr-str {:island-id iid :error (:error reply)}))
                      (swap! state assoc :status :idle :island-id nil))
                  (do (swap! state assoc
                             :island-id   iid
                             :presentation-id (:presentation-id reply)
                             :subscriptions (vec (:subscriptions reply))
                             :version     (:meta-ui-version reply)
                             :widget-tree (:widget-tree reply)
                             :sources     (or (:sources reply) {})
                             :rules       (:meta-ui-rules reply)
                             :status      :attached)
                      ;; The moment George wants marked: not when the island's DATA
                      ;; arrived, but when the preview has actually PAINTED it. reagent
                      ;; re-renders island-view on the next animation frame from the
                      ;; :attached swap; a second rAF fires AFTER that paint, so this
                      ;; "preview rendered <island>" is the real end-to-end select→shown.
                      (js/requestAnimationFrame
                        (fn [] (js/requestAnimationFrame
                                 #(activity/record! {:kind :ui :label "preview rendered"
                                                     :detail (str iid)}))))
                      ;; refresh the session selector's list for this island
                      (refresh-sessions! state real-bus)
                      ;; …and the tree the FILES describe, which is what a
                      ;; person editing a declaration must be shown.
                      (read-file-tree! state real-bus studio-sid))))))
          (do (js/console.warn "[ikc3] island-view: no island selected for studio-sid" (str studio-sid))
              (swap! state assoc :island-id nil :status :idle)))))))

(defn- refresh-sessions!
  "Fetch the live sessions on the previewed island into state, for the session
   selector. Derived server-side from the :island-presentation frames
   (:ui/list-sessions) — the shared-session model's list (2026-07-19)."
  [state real-bus]
  (when-let [iid (:island-id @state)]
    (js-invoke real-bus "dispatch"
      {:type :ui/list-sessions :island-id iid}
      (fn [sessions] (swap! state assoc :sessions (vec sessions))))))

(defn- join-session!
  "Point the preview at an EXISTING session `sid` (another surface's) so it mirrors
   that live session — the shared-session join. Releases our current preview
   session, adopts the chosen id, re-attaches under it."
  [state real-bus studio-sid sid]
  (detach! state real-bus)
  (swap! state assoc :preview-sid sid)
  (attach! state real-bus studio-sid false))

(defn widget-island-view
  "props: {:draft-source <keyword>  ; client draft key = the editor :text-area's
                                     ; bind source; absent = saved island only
           :empty-label  <string>}
   `real-bus` is the live bus (attach/detach/subscribe run on it); `studio-sid`
   is the STUDIO session; `render-fn` is render.cljs's render-node
   ([bus session-id node] → hiccup)."
  [_initial-props real-bus studio-sid render-fn]
  ;; :zoom is the render-view zoom (F-graph-ui-v0-005-01 canvas primitive): :fit
  ;; scales the whole island to the pane (for a non-responsive island like Studio
  ;; itself); a number is a fixed factor. pane-w is the live pane width (measured,
  ;; ResizeObserver) so :fit tracks a dragged divider. CSS `zoom` (not transform)
  ;; is used deliberately — it shrinks the LAYOUT box so the scaled island fits
  ;; with no phantom scroll (verified headless, dev/layout_probe.mjs).
  (let [state    (r/atom {:preview-sid (random-uuid) :status :idle :zoom :fit
                          :selected-widget-id nil})
        pane-w   (r/atom nil)
        observer (atom nil)]
    (r/create-class
      {:display-name "island-view"
       :component-did-mount
       (fn [_this]
         (attach! state real-bus studio-sid false)
         (when real-bus
           ;; Studio changed which island it is editing (select / apply) — if the
           ;; SELECTED island actually changed, re-point the preview at it. Watch
           ;; ONLY :ui-island-description: it already fires on every select, so
           ;; also watching :ui-widget-tree just double-triggered this check (two
           ;; preview-contexts per select). And check with the CHEAP
           ;; :ui/selected-island, not the 5-10s :ui/preview-context (2026-07-18).
           (js-invoke real-bus "subscribe" :ui/source-changed
             (fn [ev]
               ;; THE PREVIEWED SESSION'S OWN DATA, before anything else.
               (let [{:keys [status subscriptions preview-sid]} @state]
                 (when (= :attached status)
                   (reread! state real-bus
                            (pairs-to-reread subscriptions preview-sid ev))))
               (when (= :ui-island-description (:source ev))
                 ;; THE FILES MAY HAVE MOVED EVEN WHEN THE ISLAND HAS NOT.
                 ;; :ui-island-description fires on every select and every
                 ;; apply, and the selection commands clear :ui-preview-tree's
                 ;; cache at the same moment. Without this read the preview
                 ;; asked for the file tree ONCE, at attach, and then showed
                 ;; that answer for ever — measured 2026-09-21: a pane title
                 ;; changed in dsl/patterns/reader-v3.edn did not appear.
                 (read-file-tree! state real-bus studio-sid)
                 (js-invoke real-bus "dispatch"
                   {:type :ui/selected-island :session-id studio-sid}
                   (fn [sel]
                     (when (not= (:island-id sel) (:island-id @state))
                       ;; Island CHANGED → mint a FRESH preview session (true), so
                       ;; the Live Preview starts each island in a NEW session of
                       ;; its own, never the parent studio's and never the prior
                       ;; island's (George, 2026-07-19). Data-only refreshes below
                       ;; keep the same session (false).
                       (attach! state real-bus studio-sid true)))))))
           ;; The PREVIEWED island's own tree/data changed — refresh this session.
           (js-invoke real-bus "subscribe" :ui/widget-tree-changed
             (fn [ev]
               (when (= (:island-id ev) (:island-id @state))
                 (attach! state real-bus studio-sid false))))
           ;; Track the Studio session's selected widget so the Live preview can
           ;; highlight it. :ui/widget-selected fires on every :ui/select-widget
           ;; (tree row click, canvas click, or programmatic). Only react to
           ;; events for THIS Studio session.
           (js-invoke real-bus "subscribe" :ui/widget-selected
             (fn [ev]
               (when (= studio-sid (:session-id ev))
                 (swap! state assoc :selected-widget-id (:widget-id ev)))))))
       :component-will-unmount
       (fn [_this] (detach! state real-bus) (some-> @observer (.disconnect)))
       :reagent-render
       (fn [props _real-bus _studio-sid _render-fn]
         ;; :widget-tree — the key attach! actually writes above. Reading
         ;; :description here made the live preview permanently blank unless a
         ;; parseable draft happened to override it (same missed ea698c7
         ;; rename as core.cljs/on-attached).
         (let [{:keys [island-id preview-sid widget-tree file-tree sources rules status select?
                       selected-widget-id]} @state
               {:keys [draft-source empty-label]} props
               dr   (when draft-source (draft/draft studio-sid draft-source))
               ;; THREE SOURCES, IN THIS ORDER, AND EACH IS FRESHER THAN THE
               ;; NEXT (PLAN-graph-ui-v0-016-02 phase 9).
               ;;
               ;;   the DRAFT      what is being typed, this second
               ;;   the FILES      what is saved on disk, unseeded
               ;;   the GRAPH      what the island was last seeded at
               ;;
               ;; The graph was the only one until the draft arrived, and the
               ;; files were missing in between: a saved page file previewed
               ;; only after a re-seed, so an edit looked as though it had done
               ;; nothing.
               tree (or (when (and dr (:parsed dr)) (:parsed dr))
                        file-tree
                        widget-tree)
               err  (when dr (:error dr))
               ;; Select mode: a click on a previewed widget locates it in the
               ;; Source editor (via the locate channel, keyed by draft-source) and
               ;; mirrors the selection into the Widget-tree / Selected-widget panels
               ;; (:ui/select-widget on the STUDIO session). Both hang off one
               ;; data-widget-id stamped by the instrumented render.
               on-canvas-click
               (fn [e]
                 (when-let [el (and select? draft-source
                                    (.closest (.-target e) "[data-widget-id]"))]
                   (.stopPropagation e)
                   (let [wid (.getAttribute el "data-widget-id")]
                     (locate/request! studio-sid draft-source wid)
                     (when real-bus
                       (js-invoke real-bus "dispatch"
                         {:type :ui/select-widget :session-id studio-sid :widget-id wid}
                         (fn [_] nil))))))
               ;; The previewed island renders at a fixed DESIGN size (dw × dh) — a
               ;; desktop width where every island lays out CLEANLY (Studio needs
               ;; ~1450; 1920×1080 is verified clean via dev/layout_probe.mjs) — then
               ;; CSS `zoom` scales that whole clean render to the pane. Using the
               ;; live viewport width instead rendered a cramped, self-overflowing
               ;; island BEFORE zoom (found headless 2026-07-17). :fit tracks the
               ;; live pane width so a dragged divider re-fits.
               zoom-set (:zoom @state)
               dw       1920
               dh       1080
               pw       (or @pane-w dw)
               factor   (if (= :fit zoom-set) (min 1 (/ pw dw)) zoom-set)
               zoom-val (if (= :fit zoom-set) "fit" (str zoom-set))]
           [:div.flex.flex-col.flex-1.min-h-0
            ;; ---- canvas chrome: session id + lifecycle buttons + Select toggle
            [:div.flex.flex-row.items-center.gap-2.text-xs.shrink-0.mb-1
             {:class "text-[var(--faint)]"}
             ;; Session SELECTOR: which session this preview mirrors. Options are
             ;; the live sessions on this island (:ui/list-sessions) — pick another
             ;; surface's to see the SAME live state, or "＋ new session" to start a
             ;; fresh one (George's shared-session model, 2026-07-19).
             ;; Options are spread POSITIONALLY via `into`, never handed back as
             ;; one nested vector-of-options: a CLJS vector implements IFn, so
             ;; `[:select attrs [[:option ..] ..]]` makes Reagent index-look-up
             ;; the inner vector and throw "Key must be integer" (CLAUDE.md's
             ;; hiccup gotcha; caught headless by studio-test, not in a browser).
             (into [:select.rounded.border.bg-transparent
                    {:style {:border-color "var(--border)"}
                     :title "Session shown in this preview — pick another surface's to mirror it, or start a new one"
                     :value (str preview-sid)
                     :on-change (fn [e]
                                  (let [v (.. e -target -value)]
                                    (cond
                                      (= v "__new__")            (attach! state real-bus studio-sid true)
                                      (not= v (str preview-sid)) (join-session! state real-bus studio-sid (uuid v)))))}
                    ^{:key "cur"}
                    [:option {:value (str preview-sid)}
                     (str "session " (subs (str preview-sid) 0 8) "…"
                          (when (= :attaching status) " · attaching"))]]
                   (conj
                    (vec (for [{:keys [value label]} (:sessions @state)
                               ;; Hide the current preview session (shown above as
                               ;; the "cur" option) AND the PARENT Studio session —
                               ;; the self-embed guard: when Studio previews Studio,
                               ;; the parent's session is on the same island and
                               ;; would appear here; mirroring it would make the
                               ;; preview and the parent share one session, so
                               ;; clicks in the preview dispatch on the parent's
                               ;; behalf.  Blocked: a preview is its own surface.
                               :when (and (not= value (str preview-sid))
                                          (not= value (str studio-sid)))]
                           ^{:key value} [:option {:value value} label]))
                    ^{:key "new"} [:option {:value "__new__"} "＋ new session"]))
             [:button.px-2.py-0.5.rounded.border
              {:style {:border-color "var(--border)"}
               :on-click (fn [_] (attach! state real-bus studio-sid false))}
              "Reconnect"]
             ;; Select mode toggle — when on, clicking a widget in the canvas jumps
             ;; the Source editor's cursor to that node (and selects its span).
             [:button.px-2.py-0.5.rounded.border
              {:style (if select?
                        {:border-color "var(--accent)" :background "var(--accent)" :color "var(--field)"}
                        {:border-color "var(--border)"})
               :title "Click a widget in the preview to find it in the Source editor"
               :on-click (fn [_] (swap! state update :select? not))}
              (if select? "Select ✓" "Select")]
             ;; Zoom selector — the render-view zoom control (canvas primitive).
             ;; Fit = the whole island in the pane, for a non-responsive island
             ;; (e.g. Studio previewing itself); the % steps are a fixed factor.
             [:select.rounded.border.bg-transparent.ml-auto
              {:style {:border-color "var(--border)"}
               :title "Zoom the previewed island — Fit shows the whole island in the pane"
               :value zoom-val
               :on-change (fn [e]
                            (let [v (.. e -target -value)]
                              (swap! state assoc :zoom (if (= v "fit") :fit (js/parseFloat v)))
                              ;; Zoom is a client-only event (never hits the bus),
                              ;; so the activity log only sees it if we record it.
                              (activity/record! {:kind :ui :label "zoom"
                                                 :detail (if (= v "fit") "fit" (str (js/Math.round (* 100 (js/parseFloat v))) "%"))})))}
              [:option {:value "fit"} "Fit"]
              [:option {:value "1"} "100%"]
              [:option {:value "0.75"} "75%"]
              [:option {:value "0.5"} "50%"]
              [:option {:value "0.25"} "25%"]]
             (when select?
               [:span {:class "text-[var(--accent)]"} "click a widget →"])]
            ;; ---- the canvas
            (cond
              (nil? island-id)
              [:div.p-3.text-sm {:class "text-[var(--faint)]"}
               (or empty-label "select an island — its live render appears here")]

              (nil? tree)
              [:div.p-3.text-sm {:class "text-[var(--faint)]"} "attaching…"]

              :else
              (let [safe     (neutralize-nested-previews tree)
                    resolved (bind/resolve-bindings (or sources {}) safe
                                                    {'$session-id preview-sid})
                    rtree    (interpret/render-tree resolved rules)]
                [:div.flex.flex-col.flex-1.min-h-0
                 (when err
                   [:div.px-2.py-1.text-xs.shrink-0.rounded.mb-1.border
                    {:class "text-[var(--danger)]" :style {:border-color "var(--danger)"}}
                    (str "draft doesn't parse — showing last saved: " err)])
                 ;; The canvas host MUST be a flex column (like render-tree's own
                 ;; root wrapper): the previewed root carries :layout-self
                 ;; {:grow 1 :basis :zero}, and grow/basis are flex-ITEM props that
                 ;; are inert unless their parent is a flex container. Without
                 ;; `flex flex-col` here the previewed island collapsed to nothing
                 ;; (notes rendered in its own tab but not in Studio, 2026-07-17).
                 ;; In Select mode the render is INSTRUMENTED (each widget stamped
                 ;; with data-widget-id) and one delegated click locates the node —
                 ;; a crosshair cursor signals the mode.
                 ;; The zoom wrapper renders the island at STANDALONE size (dw × dh,
                 ;; a real flex column so :layout-self fills as it does standalone),
                 ;; then CSS `zoom` scales the whole box — so :fit fits with no
                 ;; phantom scroll and 100% scrolls as ONE clean bar. The pane's
                 ;; clientWidth (minus the p-2 gutter) is measured here for :fit.
                 [:div.flex-1.flex.flex-col.min-h-0.overflow-auto.rounded-md.p-2.border
                  {:ref       (fn [el]
                                (when (and el (nil? @observer))
                                  (reset! pane-w (max 1 (- (.-clientWidth el) 16)))
                                  (let [ro (js/ResizeObserver.
                                             (fn [_] (reset! pane-w (max 1 (- (.-clientWidth el) 16)))))]
                                    (.observe ro el)
                                    (reset! observer ro))))
                   :class     (str "bg-[var(--field)]" (when select? " cursor-crosshair"))
                   :style     {:border-color "var(--border)"}
                   :on-click  on-canvas-click}
                  [:div {:style {:width (str dw "px") :height (str dh "px") :zoom factor
                                 :display "flex" :flex-direction "column"}}
                   ;; THE PREVIEWED ISLAND GETS A READING BUS, and the canvas
                   ;; chrome above keeps the real one. See reading-bus.
                   (render-fn (reading-bus real-bus) preview-sid rtree
                              select? selected-widget-id)]]]))]))})))
